import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (accessToken) => {
    try {
      const response = await axios.post(`${API}/auth/session`, {
        access_token: accessToken
      }, { withCredentials: true });

      if (response.data.success && response.data.user) {
        setUser(response.data.user);

        // ── Check for pending invite stored before login ──────────────────
        let inviteToken = null;
        try {
          const raw = localStorage.getItem('mb_pending_invite');
          if (raw) {
            const { token: t, ts } = JSON.parse(raw);
            localStorage.removeItem('mb_pending_invite'); // always clear
            if (t && Date.now() - ts < 10 * 60 * 1000) {
              inviteToken = t;
            }
          }
        } catch { /* ignore */ }

        if (inviteToken) {
          try {
            const acceptRes = await axios.post(
              `${API}/groups/accept-invite`,
              { token: inviteToken },
              { withCredentials: true }
            );
            if (acceptRes.data.success && acceptRes.data.group_id) {
              window.location.href = `/groups/${acceptRes.data.group_id}`;
              return;
            }
          } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail === 'wrong_account') {
              // Logged in with wrong account — show mismatch screen
              window.location.href = `/groups/invite/${inviteToken}`;
              return;
            }
            // Token expired / already used — fall through to dashboard
          }
        }

        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const setAuthUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setAuthUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}